import { useState, useEffect } from 'react';
import styles from './Footer.module.css';

// ── Gestión del consentimiento en localStorage ──────────────
const COOKIE_KEY = 'eduportal_cookie_consent';

function getConsent() {
  try { return JSON.parse(localStorage.getItem(COOKIE_KEY)); } catch { return null; }
}
function saveConsent(obj) {
  localStorage.setItem(COOKIE_KEY, JSON.stringify(obj));
}

export default function Footer() {
  const [modal, setModal]           = useState(null); // 'privacidad' | 'condiciones' | 'politica_cookies' | 'configurar_cookies'
  const [showBanner, setShowBanner] = useState(false);
  const [analytics, setAnalytics]   = useState(true); // estado del toggle de analíticas

  useEffect(() => {
    const consent = getConsent();
    if (!consent) setShowBanner(true);
    else setAnalytics(consent.analytics ?? true);
  }, []);

  const aceptarTodas = () => {
    saveConsent({ tecnicas: true, analytics: true, fecha: new Date().toISOString() });
    setShowBanner(false);
    setModal(null);
  };

  const rechazarOpcionales = () => {
    saveConsent({ tecnicas: true, analytics: false, fecha: new Date().toISOString() });
    setShowBanner(false);
    setModal(null);
  };

  const guardarConfiguracion = () => {
    saveConsent({ tecnicas: true, analytics, fecha: new Date().toISOString() });
    setShowBanner(false);
    setModal(null);
  };

  const abrirConfigurar = () => {
    const consent = getConsent();
    if (consent) setAnalytics(consent.analytics ?? true);
    setModal('configurar_cookies');
    setShowBanner(false);
  };

  return (
    <>
      <footer className={styles.footer}>
        <div className={styles.left}>
          <span className={styles.copyright}>
            © 2026 EduPortal UAL — Juan Francisco Sánchez García. Todos los derechos reservados.
          </span>
        </div>
        <div className={styles.right}>
          <button className={styles.link} onClick={() => setModal('privacidad')}>Política de privacidad</button>
          <span className={styles.sep}>·</span>
          <button className={styles.link} onClick={() => setModal('condiciones')}>Condiciones de uso</button>
          <span className={styles.sep}>·</span>
          <button className={styles.link} onClick={() => setModal('politica_cookies')}>Política de cookies</button>
          <span className={styles.sep}>·</span>
          <button className={styles.link} onClick={abrirConfigurar}>Configurar cookies</button>
        </div>
      </footer>

      {/* ── Banner de cookies ── */}
      {showBanner && (
        <div className={styles.cookieBanner}>
          <div className={styles.cookieBannerContent}>
            <p className={styles.cookieBannerText}>
              🍪 Utilizamos cookies para mejorar tu experiencia de navegación, analizar el tráfico del sitio y personalizar el contenido. Puedes aceptar todas las cookies, rechazar las opcionales o configurarlas según tus preferencias.
            </p>
            <div className={styles.cookieBannerActions}>
              <button className={styles.btnConfigurar} onClick={abrirConfigurar}>Configurar</button>
              <button className={styles.btnRechazar}   onClick={rechazarOpcionales}>Rechazar opcionales</button>
              <button className={styles.btnAceptar}    onClick={aceptarTodas}>Aceptar todas</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modales ── */}
      {modal && modal !== 'configurar_cookies' && (
        <div className={styles.overlay} onClick={() => setModal(null)}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {modal === 'privacidad'       && '🔒 Política de Privacidad'}
                {modal === 'condiciones'      && '📋 Condiciones de Uso'}
                {modal === 'politica_cookies' && '🍪 Política de Cookies'}
              </h2>
              <button className={styles.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>
            <div className={styles.modalContent}>
              {modal === 'privacidad'       && <TextoPrivacidad />}
              {modal === 'condiciones'      && <TextoCondiciones />}
              {modal === 'politica_cookies' && <TextoCookies />}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal configurar cookies ── */}
      {modal === 'configurar_cookies' && (
        <div className={styles.overlay} onClick={() => setModal(null)}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>🍪 Configuración de cookies</h2>
              <button className={styles.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>
            <div className={styles.modalContent}>
              {/* Técnicas — bloqueadas */}
              <div className={styles.cookieCategoria}>
                <div className={styles.cookieCategoriaHeader}>
                  <div>
                    <h3 className={styles.cookieCategoriaTitulo}>Cookies Técnicas (Necesarias)</h3>
                    <p className={styles.cookieCategoriaDesc}>Son esenciales para el funcionamiento del sitio web. Incluyen cookies de sesión que permiten tu autenticación y navegación segura. No se pueden desactivar.</p>
                  </div>
                  <button className={`${styles.cookieToggle} ${styles.cookieToggleLocked}`} disabled>
                    <span className={styles.cookieToggleKnob} />
                  </button>
                </div>
                <div className={styles.cookieEjemplo}>
                  <span className={styles.cookieEjemploLabel}>Ejemplo:</span> Cookie de sesión JWT (jwt_token) — Duración: Sesión
                </div>
              </div>

              {/* Analíticas — configurables */}
              <div className={styles.cookieCategoria}>
                <div className={styles.cookieCategoriaHeader}>
                  <div>
                    <h3 className={styles.cookieCategoriaTitulo}>Cookies Analíticas (Terceros)</h3>
                    <p className={styles.cookieCategoriaDesc}>Nos permiten analizar el uso del sitio web para medir y mejorar el rendimiento. Recopilan información de forma anónima sobre cómo los visitantes utilizan nuestro sitio.</p>
                  </div>
                  <button
                    className={`${styles.cookieToggle} ${analytics ? styles.cookieToggleOn : styles.cookieToggleOff}`}
                    onClick={() => setAnalytics(p => !p)}>
                    <span className={styles.cookieToggleKnob} />
                  </button>
                </div>
                <div className={styles.cookieEjemplo}>
                  <span className={styles.cookieEjemploLabel}>Proveedor:</span> Google Analytics (_ga, _gid, _gat) — Duración: Hasta 2 años
                </div>
              </div>
            </div>

            {/* Acciones del modal de configuración */}
            <div className={styles.cookieConfigActions}>
              <button className={styles.btnOcultarConf} onClick={() => { setModal(null); setShowBanner(true); }}>
                Ocultar configuración
              </button>
              <button className={styles.btnRechazar}    onClick={rechazarOpcionales}>Rechazar opcionales</button>
              <button className={styles.btnAceptar}     onClick={aceptarTodas}>Aceptar todas</button>
              <button className={styles.btnGuardarConf} onClick={guardarConfiguracion}>Guardar configuración</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
//  POLÍTICA DE COOKIES
// ============================================================
function TextoCookies() {
  return (
    <>
      <p className={styles.modalDate}>Última actualización: mayo de 2026</p>
      <p>En cumplimiento del artículo 22.2 de la <strong>Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI)</strong> y del <strong>Reglamento (UE) 2016/679 (RGPD)</strong>, le informamos sobre el uso de cookies en EduPortal UAL.</p>

      <h3>1. ¿Qué son las cookies?</h3>
      <p>Las cookies son pequeños archivos de texto que los sitios web almacenan en el navegador del usuario con el fin de recordar información sobre su visita. Pueden ser cookies propias (establecidas por el sitio web visitado) o de terceros (establecidas por un dominio diferente).</p>

      <h3>2. Tipos de cookies que utilizamos</h3>
      <h4>2.1 Cookies técnicas o necesarias (no requieren consentimiento)</h4>
      <p>Son imprescindibles para el correcto funcionamiento de la plataforma. Sin ellas, no sería posible la autenticación ni la navegación segura.</p>
      <table className={styles.legalTable}>
        <thead><tr><th>Nombre</th><th>Tipo</th><th>Finalidad</th><th>Duración</th></tr></thead>
        <tbody>
          <tr>
            <td><strong>jwt_token</strong></td>
            <td>Propia / httpOnly</td>
            <td>Token de autenticación del usuario. Permite mantener la sesión iniciada de forma segura sin ser accesible desde JavaScript.</td>
            <td>Sesión (8 horas)</td>
          </tr>
        </tbody>
      </table>

      <h4>2.2 Cookies analíticas de terceros (requieren consentimiento)</h4>
      <p>Estas cookies permiten analizar el comportamiento de los usuarios para mejorar el rendimiento del sitio. Solo se activan si el usuario otorga su consentimiento.</p>
      <table className={styles.legalTable}>
        <thead><tr><th>Nombre</th><th>Proveedor</th><th>Finalidad</th><th>Duración</th></tr></thead>
        <tbody>
          <tr><td><strong>_ga</strong></td><td>Google Analytics</td><td>Distingue usuarios únicos mediante un identificador anónimo.</td><td>2 años</td></tr>
          <tr><td><strong>_gid</strong></td><td>Google Analytics</td><td>Distingue usuarios. Se actualiza cada 24 horas.</td><td>24 horas</td></tr>
          <tr><td><strong>_gat</strong></td><td>Google Analytics</td><td>Limita la tasa de solicitudes a Google Analytics.</td><td>1 minuto</td></tr>
        </tbody>
      </table>

      <h3>3. ¿Cómo gestionar las cookies?</h3>
      <p>Puede gestionar sus preferencias de cookies en cualquier momento a través del enlace <strong>"Configurar cookies"</strong> situado en el pie de página de la plataforma.</p>
      <p>Asimismo, puede configurar su navegador para bloquear o eliminar las cookies. A continuación le indicamos cómo hacerlo en los principales navegadores:</p>
      <ul>
        <li><strong>Google Chrome:</strong> Configuración → Privacidad y seguridad → Cookies y otros datos de sitios</li>
        <li><strong>Mozilla Firefox:</strong> Opciones → Privacidad y seguridad → Cookies y datos del sitio</li>
        <li><strong>Safari:</strong> Preferencias → Privacidad → Gestionar datos del sitio web</li>
        <li><strong>Microsoft Edge:</strong> Configuración → Privacidad, búsqueda y servicios → Cookies</li>
      </ul>
      <p>Tenga en cuenta que bloquear las cookies técnicas puede afectar al funcionamiento de la plataforma, impidiendo el inicio de sesión y la navegación correcta.</p>

      <h3>4. Transferencias internacionales</h3>
      <p>Las cookies de Google Analytics implican una transferencia de datos a Google LLC, con sede en EE.UU. Google LLC está adherida al Marco de Privacidad de Datos UE-EE.UU. (EU-U.S. Data Privacy Framework), lo que garantiza un nivel de protección adecuado conforme al artículo 45 del RGPD.</p>

      <h3>5. Actualizaciones de la política</h3>
      <p>Esta Política de Cookies puede ser actualizada en cualquier momento para adaptarse a cambios legislativos o funcionales de la plataforma. Le recomendamos revisarla periódicamente.</p>

      <h3>6. Contacto</h3>
      <p>Para cualquier consulta relacionada con el uso de cookies, puede contactarnos en <strong>administracioneduportal@gmail.com</strong>.</p>
    </>
  );
}

// ============================================================
//  POLÍTICA DE PRIVACIDAD
// ============================================================
function TextoPrivacidad() {
  return (
    <>
      <p className={styles.modalDate}>Última actualización: abril de 2026</p>
      <p>En cumplimiento del <strong>Reglamento (UE) 2016/679 del Parlamento Europeo y del Consejo, de 27 de abril de 2016</strong>, relativo a la protección de las personas físicas en lo que respecta al tratamiento de datos personales (RGPD), y de la <strong>Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD)</strong>, le informamos de los siguientes extremos relativos al tratamiento de sus datos personales.</p>
      <h3>1. Responsable del tratamiento</h3>
      <table className={styles.legalTable}>
        <tbody>
          <tr><td><strong>Denominación</strong></td><td>EduPortal UAL</td></tr>
          <tr><td><strong>Titular</strong></td><td>Juan Francisco Sánchez García</td></tr>
          <tr><td><strong>Carácter</strong></td><td>Proyecto académico — Trabajo de Fin de Grado</td></tr>
          <tr><td><strong>Centro</strong></td><td>Universidad de Almería (UAL)</td></tr>
          <tr><td><strong>Contacto</strong></td><td>administracioneduportal@gmail.com</td></tr>
        </tbody>
      </table>
      <h3>2. Finalidades del tratamiento y base jurídica</h3>
      <p><strong>a) Gestión de la cuenta de usuario</strong><br/>Base jurídica: Ejecución de una relación contractual/académica (art. 6.1.b RGPD).</p>
      <p><strong>b) Gestión académica</strong><br/>Base jurídica: Interés legítimo en la gestión académica universitaria (art. 6.1.f RGPD).</p>
      <p><strong>c) Comunicaciones de la plataforma</strong><br/>Base jurídica: Consentimiento del interesado (art. 6.1.a RGPD).</p>
      <h3>3. Datos personales tratados</h3>
      <ul>
        <li><strong>Datos identificativos:</strong> nombre, apellidos, DNI/NIF, fecha de nacimiento</li>
        <li><strong>Datos de contacto:</strong> correo electrónico, dirección postal, código postal, provincia, país</li>
        <li><strong>Datos académicos:</strong> asignaturas, trabajos, calificaciones, mensajes</li>
        <li><strong>Datos de acceso:</strong> contraseña cifrada (bcrypt), token de sesión (cookie httpOnly)</li>
        <li><strong>Imagen:</strong> fotografía de perfil (voluntaria)</li>
        <li><strong>Datos técnicos:</strong> dirección IP (solo con fines de seguridad)</li>
      </ul>
      <h3>4. Destinatarios de los datos</h3>
      <p>No se cederán datos a terceros salvo obligación legal. Para el envío de notificaciones se utiliza el servicio SMTP de Google LLC (Gmail), conforme a las garantías del RGPD.</p>
      <h3>5. Plazo de conservación</h3>
      <p>Los datos se conservarán mientras la cuenta esté activa. Tras la baja, se conservarán bloqueados durante <strong>un (1) año</strong> y posteriormente se eliminarán.</p>
      <h3>6. Derechos de los interesados</h3>
      <p>Puede ejercer los derechos de acceso, rectificación, supresión, limitación, portabilidad, oposición y retirada del consentimiento escribiendo a <strong>administracioneduportal@gmail.com</strong>. También puede reclamar ante la <strong>AEPD</strong> en <strong>www.aepd.es</strong>.</p>
      <h3>7. Medidas de seguridad</h3>
      <ul>
        <li>Cifrado de contraseñas bcrypt (coste 12)</li>
        <li>Autenticación JWT con cookie httpOnly</li>
        <li>Consultas parametrizadas PDO (anti SQL injection)</li>
        <li>Sanitización de entradas (anti XSS)</li>
        <li>Control de acceso basado en roles (RBAC)</li>
        <li>Rate limiting en endpoints sensibles</li>
        <li>Validación de tipo MIME en subida de archivos</li>
        <li>Cabeceras HTTP de seguridad</li>
      </ul>
      <h3>8. Uso de cookies</h3>
      <p>EduPortal UAL utiliza una cookie técnica de sesión (<strong>jwt_token</strong>) imprescindible para la autenticación segura del usuario. Con su consentimiento, también puede utilizar cookies analíticas de terceros. Consulte nuestra <strong>Política de Cookies</strong> para más información.</p>
      <h3>9. Carácter académico de la plataforma</h3>
      <p>EduPortal UAL es un proyecto desarrollado con fines exclusivamente académicos en el marco de un Trabajo de Fin de Grado de la Universidad de Almería. Los datos no serán utilizados para ninguna finalidad comercial.</p>
    </>
  );
}

// ============================================================
//  CONDICIONES DE USO
// ============================================================
function TextoCondiciones() {
  return (
    <>
      <p className={styles.modalDate}>Última actualización: abril de 2026</p>
      <p>Las presentes Condiciones Generales de Uso regulan el acceso, navegación y utilización de <strong>EduPortal UAL</strong>, desarrollada por Juan Francisco Sánchez García como Trabajo de Fin de Grado de la Universidad de Almería.</p>
      <h3>1. Identificación del titular</h3>
      <table className={styles.legalTable}>
        <tbody>
          <tr><td><strong>Titular</strong></td><td>Juan Francisco Sánchez García</td></tr>
          <tr><td><strong>Plataforma</strong></td><td>EduPortal UAL — Portal Educativo Universitario</td></tr>
          <tr><td><strong>Ámbito</strong></td><td>Proyecto académico — Universidad de Almería</td></tr>
          <tr><td><strong>Contacto</strong></td><td>administracioneduportal@gmail.com</td></tr>
        </tbody>
      </table>
      <h3>2. Objeto</h3>
      <p>EduPortal UAL es una plataforma educativa de gestión académica universitaria que permite la comunicación entre alumnos y profesores, gestión de asignaturas, entrega y corrección de trabajos, publicación de calificaciones y distribución de materiales docentes.</p>
      <h3>3. Acceso y registro</h3>
      <p>El usuario se compromete a facilitar datos verídicos, custodiar sus credenciales y no compartirlas con terceros. El titular no será responsable de los daños derivados del incumplimiento de estas obligaciones.</p>
      <h3>4. Roles de usuario</h3>
      <p><strong>Administrador:</strong> gestión completa del sistema. <strong>Profesor:</strong> gestión de contenidos académicos de sus asignaturas. <strong>Alumno:</strong> acceso a contenidos y entrega de trabajos en los plazos establecidos.</p>
      <h3>5. Obligaciones del usuario</h3>
      <p>Queda expresamente prohibido: publicar contenidos contrarios a la ley, suplantar identidades, intentar accesos no autorizados, realizar ataques informáticos, entregar trabajos plagiados o utilizar la plataforma con fines comerciales.</p>
      <h3>6. Propiedad intelectual</h3>
      <p>EduPortal UAL y todos sus elementos son propiedad intelectual de Juan Francisco Sánchez García, protegida por la normativa española e internacional. Queda prohibida su reproducción sin autorización expresa.</p>
      <h3>7. Protección de datos</h3>
      <p>El tratamiento de datos se rige por la Política de Privacidad de EduPortal UAL, que el usuario declara haber leído y aceptado.</p>
      <h3>8. Disponibilidad del servicio</h3>
      <p>Al tratarse de un proyecto académico, el titular no garantiza la disponibilidad continuada del servicio y se reserva el derecho a interrumpirlo en cualquier momento por razones justificadas.</p>
      <h3>9. Legislación aplicable y jurisdicción</h3>
      <p>Las presentes Condiciones se rigen por la legislación española y de la UE (RGPD, LOPDGDD, LSSI, LPI). Para la resolución de controversias, las partes se someten a los Juzgados y Tribunales de Almería.</p>
    </>
  );
}
